import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function CookiesPolicy() {
  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 pt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold text-sm transition-colors mb-6">
            <ArrowLeft size={18} />
            Back to Valuation
          </Link>
          <div className="text-center mb-4">
            <img src="sell-my-car-teesside-logo.png" alt="Sell My Car Teesside Logo" className="mx-auto w-48 h-24 mb-3 object-contain" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 text-center">Cookies Policy</h1>
          <p className="text-slate-500 text-sm text-center">Last updated: March 2026</p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200 mb-8 space-y-8 text-slate-700 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">How We Use Cookies</h2>
            <p className="mb-3">We use cookies for the following purposes:</p>
            
            <h3 className="font-semibold text-slate-900 mb-2">1. Essential Website Functionality</h3>
            <p className="mb-2">These cookies are necessary for the basic operation of the website, such as submitting enquiry forms.</p>
            <p className="mb-3">Without these cookies, the website may not function correctly.</p>

            <h3 className="font-semibold text-slate-900 mb-2">2. Website Analytics</h3>
            <p className="mb-2">We use Google Analytics to understand how visitors interact with our website. Google Analytics helps us analyse:</p>
            <ul className="list-disc ml-6 space-y-1 mb-3">
              <li>Number of visitors</li>
              <li>Pages visited</li>
              <li>Time spent on pages</li>
              <li>General geographic location of visitors</li>
              <li>How users arrive at our website</li>
            </ul>
            <p className="mb-2">This data helps us improve the website and marketing performance.</p>
            <p className="mb-2">Google Analytics cookies may include:</p>
            <ul className="list-disc ml-6 space-y-1 mb-2">
              <li>_ga</li>
              <li>_ga_*</li>
              <li>_gid</li>
            </ul>
            <p className="mb-3">These cookies collect information in an anonymous and aggregated form.</p>
            <p className="mb-3">More information: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://policies.google.com/privacy</a></p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Managing Cookies</h2>
            <p className="mb-2">You can control and manage cookies through your browser settings. Most browsers allow you to:</p>
            <ul className="list-disc ml-6 space-y-1 mb-3">
              <li>View cookies stored on your device</li>
              <li>Delete cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block all cookies</li>
            </ul>
            <p className="mb-3">Please note that disabling cookies may affect how parts of the website function.</p>
            <p className="mb-3">You can also opt out of Google Analytics tracking here: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://tools.google.com/dlpage/gaoptout</a></p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Privacy & Data Processing</h2>
            <p className="mb-3">When you submit an enquiry through our website (for example to sell your car), we may collect personal information such as:</p>
            <ul className="list-disc ml-6 space-y-1 mb-3">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Vehicle information</li>
              <li>General location information</li>
            </ul>
            <p>This information is used solely for the purpose of responding to your enquiry and offering to purchase your vehicle.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">How We Store and Process Your Data</h2>
            <p className="mb-2">We may process enquiry information using:</p>
            <ul className="list-disc ml-6 space-y-1 mb-3">
              <li>Email communication</li>
              <li>Internal spreadsheets used for managing enquiries</li>
              <li>Website analytics tools</li>
            </ul>
            <p className="mb-2">Data is only accessed by authorised personnel within TeessideAutos Ltd and is not sold to third parties.</p>
            <p>We only retain personal data for as long as necessary to respond to enquiries or comply with legal obligations.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Your Data Rights</h2>
            <p className="mb-2">Under UK data protection law, you have the right to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Request access to the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to processing of your data</li>
              <li>Request restriction of processing</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, please contact us using the details below.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Contact Details</h2>
            <p className="mb-2">If you have any questions about this Cookies Policy or how we process data, please contact:</p>
            <div className="space-y-1">
              <p><strong>TeessideAutos Ltd</strong></p>
              <p><strong>Email:</strong> teessideautos1@gmail.com</p>
              <p><strong>Location:</strong> Teesside, United Kingdom</p>
              <p><strong>ICO Registration Number:</strong> ZB732531</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">Changes To This Policy</h2>
            <p>We may update this Cookies Policy from time to time. Updates will be posted on this page with a revised &ldquo;Last updated&rdquo; date.</p>
          </section>
        </div>

        <div className="text-center py-6">
          <p className="text-slate-500 text-sm">
            Powered by <a href="https://www.teessideautos.co.uk/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">TeessideAutos</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CookiesPolicy;
